import { AfterViewInit, Component, Input, Output, OnChanges, SimpleChanges, OnDestroy, OnInit } from '@angular/core';
import { CrudFormFacade, CrudFormMode, CrudFormUiState, DEFAULT_CRUD_FORM_MODE } from './crud-forms.facade';
import { FormGroup, FormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { DropdownModule } from 'primeng/dropdown';
import { EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormStatus } from '../../global-data-models/form.model';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { SurveyCreatorModule } from 'survey-creator-angular'; // Keep SurveyCreatorModule for the component template
import { SurveyCreatorModel } from 'survey-creator-core';
import { SurveyModule } from 'survey-angular-ui'; // Add SurveyModule for preview
import { SurveyModel } from 'survey-core';
import { APIRoutes } from '../../global-misc/api-routes';
import { ImageUploadService } from '../../global-services/image-upload.service';

@Component({
  selector: 'app-crud-forms',
  standalone: true,
  imports: [
    ...COMMON_IMPORTS,
    DropdownModule,
    InputTextareaModule,
    InputSwitchModule,
    DialogModule,
    FormsModule,
    TooltipModule,
    ConfirmDialogModule,
    SurveyCreatorModule,
    SurveyModule,
  ],
  templateUrl: './crud-forms.component.html',
  styleUrl: './crud-forms.component.css',
})
export class CrudFormsComponent implements OnInit, OnChanges, OnDestroy {
  public readonly CrudFormMode = CrudFormMode;

  @Input() crudMode: CrudFormMode = DEFAULT_CRUD_FORM_MODE;
  @Input() formId?: string;

  private onSuccessEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() onSuccess: Observable<void> = this.onSuccessEvent.asObservable();

  public form: FormGroup;
  public statuses: FormStatus[] = [];
  public statusOptions: { label: string; value: string }[] = [];
  public currentCrudMode: CrudFormMode = DEFAULT_CRUD_FORM_MODE;
  public showFormDataDialog: boolean = false;
  public dialogFormData: string = '';
  public isValidJson: boolean = true;
  public jsonValidationError: string = '';
  public showSurveyCreatorDialog: boolean = false;
  public surveyCreatorModel?: SurveyCreatorModel;
  public showSurveyPreviewDialog: boolean = false;
  public surveyPreviewModel?: SurveyModel;
  public isMobilePreview: boolean = false;

  private sub: Subscription = new Subscription();
  private submitSub = new Subscription();

  private orgId: string;

  public constructor(
    private readonly facade: CrudFormFacade,
    private readonly route: ActivatedRoute,
    private readonly messageService: GlobalMessageService,
    private readonly imageUploadService: ImageUploadService,
    private readonly confirmationService: ConfirmationService,
  ) {
    this.form = this.facade.uiState.form;
    this.orgId = this.route.snapshot.paramMap.get('orgId') as string;
  }

  public ngOnInit(): void {
    this.sub.add(
      this.facade.uiState$.subscribe((state: CrudFormUiState) => {
        this.form = state.form;
        this.statuses = state.statuses;
        this.currentCrudMode = state.crudMode;
        this.statusOptions = this.statuses.map((status) => ({
          label: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
          value: status,
        }));
        console.log('Statuses array:', this.statuses);
        console.log('Status options:', this.statusOptions);
        console.log('Current status value:', this.form.get('status')?.value);
      }),
    );

    this.facade.begin(this.orgId, this.crudMode, this.formId);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ((changes['crudMode'] && !changes['crudMode'].firstChange) || (changes['formId'] && !changes['formId'].firstChange)) {
      this.facade.begin(this.orgId, this.crudMode, this.formId);
    }
  }

  public ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.submitSub.unsubscribe();
  }

  public async onFormSubmit() {
    // if empty show error
    if (this.form.invalid) {
      this.messageService.showError('Validation Error', 'Please fill in all required fields before submitting.');
      return;
    }

    const formDataValue = this.form.get('formData')?.value;
    if (!formDataValue || formDataValue.trim() === '') {
      this.messageService.showError('Validation Error', 'Form data cannot be empty. Please create a form before submitting.');
      return;
    }

    if (this.form.get('status')?.value !== 'PUBLISHED') {
      this.confirmationService.confirm({
        message: 'This will be saved as draft and wont be assignable to patients. Are you sure you want to proceed?',
        header: 'Draft Form Confirmation',
        acceptLabel: '  Yes, Save as Draft',
        rejectLabel: ' No, let me change it to Published',
        accept: async () => {
          await this.facade.submit();
          this.onSuccessEvent.emit();
        },
        reject: () => {
          // User cancelled, do nothing
        },
      });
      return;
    }

    this.confirmationService.confirm({
      message: 'Publishing this form will make it available and assignable to patients , and will up the version number. Are you sure you want to proceed?',
      header: 'Publish Form Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: '  Yes, Publish',
      rejectLabel: ' No let me change it to draft',
      accept: async () => {
        await this.facade.submit();
        this.onSuccessEvent.emit();
      },
      reject: () => {
        // User cancelled, do nothing
      },
    });
    return;
  }

  public onCancel() {
    this.facade.resetForm();
  }

  // Methods for Form Data JSON Dialog
  public openFormDataDialog() {
    // Load current form data into dialog's local state
    this.dialogFormData = this.form.get('formData')?.value || '';
    this.validateJson();
    this.showFormDataDialog = true;
  }

  public closeFormDataDialog() {
    this.showFormDataDialog = false;
    // Reset dialog data when closing (discards any unsaved changes)
    this.dialogFormData = '';
    this.isValidJson = true;
    this.jsonValidationError = '';
  }

  public validateJson() {
    if (!this.dialogFormData.trim()) {
      this.isValidJson = true;
      this.jsonValidationError = '';
      return;
    }

    try {
      JSON.parse(this.dialogFormData);
      this.isValidJson = true;
      this.jsonValidationError = '';
    } catch (error: any) {
      this.isValidJson = false;
      this.jsonValidationError = error.message || 'Invalid JSON format';
    }
  }

  public onJsonDataChange() {
    this.validateJson();
  }

  public updateFormData() {
    if (!this.isValidJson) {
      this.messageService.showError('Validation Error', 'Please fix the JSON errors before updating the form data.');
      return;
    }

    // Update the actual form with dialog data
    this.form.get('formData')?.setValue(this.dialogFormData);
    this.messageService.showSuccess('Success', 'Form data updated');
    this.closeFormDataDialog();
  }

  public async copyFormData() {
    try {
      await navigator.clipboard.writeText(this.dialogFormData);
      this.messageService.showSuccess('Success', 'Form data copied to clipboard');
    } catch (error) {
      // Fallback for older browsers or when clipboard API is not available
      const textArea = document.createElement('textarea');
      textArea.value = this.dialogFormData;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.messageService.showSuccess('Success', 'Form data copied to clipboard');
    }
  }

  // Methods for SurveyJS Creator Dialog
  public openSurveyCreatorDialog(): void {
    const existingJsonString = this.form.get('formData')?.value; // Changed from crudForm.get('formJson') to form.get('formData')
    const creatorOptions = {
      showLogicTab: true,
      showTranslationTab: true,
      // As per docs, autoSave can be configured here if needed.
      // autoSaveEnabled: true,
    };
    this.surveyCreatorModel = new SurveyCreatorModel(creatorOptions);

    if (existingJsonString) {
      try {
        // Ensure existingJsonString is parsed if it's a string, otherwise use as is if it's already an object.
        // SurveyJS creator's 'text' property expects a JSON string.
        const formData = typeof existingJsonString === 'string' ? JSON.parse(existingJsonString) : existingJsonString;
        this.surveyCreatorModel.text = JSON.stringify(formData || {}); // Ensure it's always a string, default to empty object string if null/undefined
      } catch (error) {
        console.error('Error parsing existing form JSON for Survey Creator:', error);
        this.messageService.showError('Error', 'Could not load existing form data into Survey Creator. Initializing with an empty survey.');
        this.surveyCreatorModel.text = JSON.stringify({}); // Initialize with empty survey if parse fails
      }
    } else {
      // Initialize with an empty survey if no existing data
      this.surveyCreatorModel.text = JSON.stringify({});
    }
    this.showSurveyCreatorDialog = true;

    this.surveyCreatorModel.onUploadFile.add(async (sender, options) => {
      const file = options.files[0];
      if (!file) {
        options.callback('error'); // No file provided
        return;
      }

      try {
        const imageUrl = await this.imageUploadService.uploadImage(this.orgId, file, APIRoutes.ORG_SURVEY_IMAGES_UPLOAD_ROUTE);
        options.callback('success', imageUrl); // Success: pass status and URL
      } catch (error) {
        const errorMsg = error && typeof error === 'object' && 'message' in error ? (error as any).message : 'Failed to upload image';
        this.messageService.showError('Upload Error', errorMsg);
        console.error('Image upload failed:', error);
        options.callback('error'); // Indicate failure with error status
      }
    });
  }

  public closeSurveyCreatorDialog(): void {
    this.showSurveyCreatorDialog = false;
    this.surveyCreatorModel = undefined; // Clear the model
  }

  public saveSurveyCreatorData(): void {
    if (this.surveyCreatorModel) {
      const surveyJson = this.surveyCreatorModel.JSON; // Get JSON object from creator
      this.form.get('formData')?.setValue(JSON.stringify(surveyJson, null, 2)); // Save it as a string
      this.messageService.showSuccess('Success', 'Form data updated from SurveyJS Creator.');
      this.closeSurveyCreatorDialog();
    } else {
      this.messageService.showError('Error', 'Survey Creator model is not available.');
    }
  }

  // Methods for Survey Preview Dialog
  public openSurveyPreviewDialog(): void {
    const formDataString = this.form.get('formData')?.value;

    if (!formDataString || formDataString.trim() === '') {
      this.messageService.showError('Error', 'No form data to preview. Please create a form first.');
      return;
    }

    try {
      const formData = typeof formDataString === 'string' ? JSON.parse(formDataString) : formDataString;
      this.surveyPreviewModel = new SurveyModel(formData);
      this.showSurveyPreviewDialog = true;
    } catch (error) {
      console.error('Error parsing form data for preview:', error);
      this.messageService.showError('Error', 'Could not parse form data for preview. Please check your form JSON.');
    }
  }

  public closeSurveyPreviewDialog(): void {
    this.showSurveyPreviewDialog = false;
    this.surveyPreviewModel = undefined; // Clear the model
    this.isMobilePreview = false; // Reset mobile mode
  }

  public toggleMobilePreview(): void {
    this.isMobilePreview = !this.isMobilePreview;
  }

  public get isFormDataEmpty(): boolean {
    const formDataValue = this.form.get('formData')?.value;
    return !formDataValue || formDataValue.trim() === '';
  }
}
